import { useRef, useCallback } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
  MotionValue } from
"framer-motion";
import { Star, Gift, CreditCard, QrCode, Percent, Tag, Sparkles, ShoppingBag, ArrowRight, CheckCircle2 } from "lucide-react";
import logoOrizzontale from "@/assets/logo-orizzontale.png";
import saloneBg from "@/assets/desktop-hero-bg.jpg";
import phoneScreen from "@/assets/phone-screen-catalogo-opt2.webp";

const ease = [0.22, 1, 0.36, 1] as const;

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.7, ease, delay } }
});

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.6, ease, delay } }
});

/* ── Apple SVG ── */
const AppleIcon = () =>
<svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 shrink-0">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>;

/* ── Google Play SVG ── */
const PlayIcon = () =>
<svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 shrink-0">
    <path d="M3 20.5v-17c0-.83 1-.83 1.5-.5l15 8.5c.5.28.5 1-.0 1.28L4.5 21c-.5.28-1.5.28-1.5-.5zm2-14.18v11.36L17.17 12 5 6.32z" />
  </svg>;

/* ── Store CTA Button ── */
function StoreButton({
  icon, store, sub, variant = "dark"
}: {icon: React.ReactNode;store: string;sub: string;variant?: "dark" | "glass" | "white";}) {
  const base = "inline-flex items-center gap-3 px-5 py-3.5 rounded-full font-medium text-sm transition-all duration-300 active:scale-95 hover:scale-[1.04]";
  const styles = {
    dark: "bg-white/95 text-black hover:bg-white shadow-lg shadow-black/20",
    glass: "bg-white/10 text-white border border-white/20 backdrop-blur-md hover:bg-white/20",
    white: "bg-white text-black hover:bg-white/90 shadow-lg shadow-black/20"
  };
  return (
    <a href="#" className={`${base} ${styles[variant]}`}>
      {icon}
      <span className="flex flex-col leading-tight text-left">
        <span className="text-[10px] opacity-60">{sub}</span>
        <span className="text-[14px] font-semibold">{store}</span>
      </span>
    </a>);

}

/* ── iPhone 3D Interactive (Hero) ── */
function Phone3DInteractive() {
  const containerRef = useRef<HTMLDivElement>(null);
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const rotX = useSpring(rawX, { stiffness: 120, damping: 20 });
  const rotY = useSpring(rawY, { stiffness: 120, damping: 20 });
  const shine = useSpring(useMotionValue(0), { stiffness: 80, damping: 20 });

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / (rect.width / 2);
    const dy = (e.clientY - cy) / (rect.height / 2);
    rawY.set(dx * 22);
    rawX.set(-dy * 14);
    shine.set(dx * 0.5 + 0.5);
  }, [rawX, rawY, shine]);

  const handleMouseLeave = useCallback(() => {
    rawX.set(0);rawY.set(0);shine.set(0);
  }, [rawX, rawY, shine]);

  const PHONE_W = 270,PHONE_H = 560;

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ width: PHONE_W + 60, height: PHONE_H + 60, perspective: 1400 }}
      className="relative flex items-center justify-center cursor-none">
      <motion.div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full blur-3xl"
        style={{ width: 180, height: 40, background: "rgba(179,150,120,0.55)" }}
        animate={{ opacity: [0.4, 0.7, 0.4], scaleX: [0.9, 1.1, 0.9] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div
        animate={{ y: [0, -14, 0] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
        style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}>
        <PhoneFrame screenFront={phoneScreen} shineValue={shine} width={PHONE_W} height={PHONE_H} />
      </motion.div>
    </div>);

}

/* ── Shared Phone Frame ── */
function PhoneFrame({
  screenFront, screenBack, screenRewards, shineValue, width = 270, height = 560,
  cardFlipProgress, rewardsOpacity









}: {screenFront: string;screenBack?: string;screenRewards?: string;shineValue?: MotionValue<number>;width?: number;height?: number;cardFlipProgress?: MotionValue<number>;rewardsOpacity?: MotionValue<number>;}) {
  const defaultShine = useMotionValue(0);
  const shine = shineValue ?? defaultShine;

  // Card front: visible 0°→90°, hidden 90°→180°
  const frontOpacity = cardFlipProgress ?
  useTransform(cardFlipProgress, [0, 89, 90, 180], [1, 1, 0, 0]) :
  undefined;
  // Card back: hidden 0°→90°, visible 90°→180°
  const backOpacity = cardFlipProgress ?
  useTransform(cardFlipProgress, [0, 89, 90, 180], [0, 0, 1, 1]) :
  undefined;
  // Back image rotated -180° (flipped)
  const backRotate = cardFlipProgress ?
  useTransform(cardFlipProgress, [0, 180], [180, 0]) :
  undefined;
  // Screen cross-fade: card→rewards
  const cardScreenOpacity = rewardsOpacity ?
  useTransform(rewardsOpacity, [0, 1], [1, 0]) :
  undefined;

  return (
    <div>
      <div
        style={{
          width,
          height,
          borderRadius: 48,
          background: "linear-gradient(160deg, #2e2e2e 0%, #0d0d0d 60%, #1a1a1a 100%)",
          border: "2px solid rgba(255,255,255,0.10)",
          boxShadow: "0 60px 100px rgba(0,0,0,0.65), 0 20px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.09), inset 0 -1px 0 rgba(0,0,0,0.5)",
          position: "relative",
          overflow: "hidden"
        }}>
        <motion.div
          style={{
            position: "absolute", inset: 0, borderRadius: 48, pointerEvents: "none", zIndex: 20,
            background: useTransform(shine, [0, 1], [
            "linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 100%)",
            "linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0) 60%)"]
            )
          }} />
        {/* Side buttons */}
        <div style={{ position: "absolute", left: -3, top: 120, width: 4, height: 40, borderRadius: "2px 0 0 2px", background: "rgba(255,255,255,0.08)" }} />
        <div style={{ position: "absolute", left: -3, top: 175, width: 4, height: 60, borderRadius: "2px 0 0 2px", background: "rgba(255,255,255,0.08)" }} />
        <div style={{ position: "absolute", right: -3, top: 150, width: 4, height: 80, borderRadius: "0 2px 2px 0", background: "rgba(255,255,255,0.08)" }} />

        {/* Screen — layered: card front/back flip + rewards crossfade */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, borderRadius: 48, overflow: "hidden", background: "#E9E4DE", perspective: 800 }}>

          {/* Card layer (front + back flip) — fades out when rewards appear */}
          <motion.div style={{
            position: "absolute", inset: 0,
            opacity: cardScreenOpacity ?? 1
          }}>
            {/* Front face */}
            <motion.img
              src={screenFront}
              alt="Pagina Card"
              style={{
                position: "absolute", inset: 0,
                width: "100%", height: "100%",
                objectFit: "cover", objectPosition: "top center",
                display: "block", pointerEvents: "none", userSelect: "none",
                backfaceVisibility: "hidden",
                opacity: frontOpacity ?? 1,
                rotateY: cardFlipProgress ? useTransform(cardFlipProgress, [0, 180], [0, 180]) : undefined
              }} />
            {/* Back face */}
            {screenBack &&
            <motion.img
              src={screenBack}
              alt="Pagina Card retro QR"
              style={{
                position: "absolute", inset: 0,
                width: "100%", height: "100%",
                objectFit: "cover", objectPosition: "top center",
                display: "block", pointerEvents: "none", userSelect: "none",
                backfaceVisibility: "hidden",
                opacity: backOpacity ?? 0,
                rotateY: backRotate
              }} />
            }
          </motion.div>

          {/* Rewards layer — fades in on top */}
          {screenRewards && rewardsOpacity &&
          <motion.img
            src={screenRewards}
            alt="Pagina Ricompense"
            style={{
              position: "absolute", inset: 0,
              width: "100%", height: "100%",
              objectFit: "cover", objectPosition: "top center",
              display: "block", pointerEvents: "none", userSelect: "none",
              opacity: rewardsOpacity
            }} />
          }
        </div>
      </div>
      {/* Back face shadow */}
      <div style={{ position: "absolute", inset: 0, borderRadius: 48, background: "linear-gradient(160deg, #1a1a1a 0%, #0a0a0a 100%)", transform: "translateZ(-4px)", zIndex: -1, boxShadow: "0 80px 120px rgba(0,0,0,0.5)" }} />
    </div>);

}

/* ── Info Badge laterale ── */
function InfoBadge({
  icon, title, sub, style, opacity






}: {icon: React.ReactNode;title: string;sub: string;style?: React.CSSProperties;opacity: MotionValue<number>;}) {
  return (
    <motion.div
      style={{
        opacity,
        position: "absolute",
        background: "rgba(255,255,255,0.88)",
        border: "1px solid rgba(179,150,120,0.25)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        borderRadius: 16,
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        minWidth: 180,
        boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
        ...style
      }}>
      <div style={{
        width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
        background: "rgba(179,150,120,0.18)",
        display: "flex", alignItems: "center", justifyContent: "center"
      }}>
        {icon}
      </div>
      <div>
        <p style={{ color: "#2d1a0a", fontWeight: 700, fontSize: 13, margin: 0, lineHeight: 1.2 }}>{title}</p>
        <p style={{ color: "#6A6661", fontSize: 11, margin: 0, marginTop: 2 }}>{sub}</p>
      </div>
    </motion.div>);

}

/* ── Card Story Section ── */
function CardStorySection() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start start", "end end"] });

  // Card inside screen flip: 0→180 degrees from scroll 25%→55%
  const cardFlipRaw = useTransform(scrollYProgress, [0.25, 0.55], [0, 180]);
  const cardFlipProgress = useSpring(cardFlipRaw, { stiffness: 55, damping: 16 });

  // Rewards screen crossfade: card fades out, rewards fades in at scroll 60%→72%
  const rewardsOpacity = useSpring(
    useTransform(scrollYProgress, [0.60, 0.72], [0, 1]),
    { stiffness: 60, damping: 18 }
  );

  // Fase 0: accumulo punti — appare subito, scompare al 35%
  const phase0Opacity = useTransform(scrollYProgress, [0, 0.02, 0.28, 0.38], [0, 1, 1, 0]);
  // Fase 1: QR/identità — 30–65%
  const phase1Opacity = useTransform(scrollYProgress, [0.30, 0.40, 0.58, 0.68], [0, 1, 1, 0]);
  // Fase 2: voucher — 62–100%
  const phase2Opacity = useTransform(scrollYProgress, [0.60, 0.70, 0.95, 1], [0, 1, 1, 0]);

  // Badge fase 2 — appaiono in cascata
  const b1 = useTransform(scrollYProgress, [0.62, 0.70], [0, 1]);
  const b2 = useTransform(scrollYProgress, [0.66, 0.74], [0, 1]);
  const b3 = useTransform(scrollYProgress, [0.70, 0.78], [0, 1]);
  const b4 = useTransform(scrollYProgress, [0.74, 0.82], [0, 1]);

  // Dots progress
  const dot0 = useTransform(scrollYProgress, [0, 0.02, 0.33, 0.36], [6, 20, 20, 6]);
  const dot1 = useTransform(scrollYProgress, [0.33, 0.36, 0.64, 0.67], [6, 20, 20, 6]);
  const dot2 = useTransform(scrollYProgress, [0.64, 0.67, 0.96, 1], [6, 20, 20, 6]);
  const dot0Color = useTransform(scrollYProgress, [0, 0.02, 0.36], ["rgba(100,80,60,0.3)", "rgba(179,150,120,0.9)", "rgba(100,80,60,0.3)"]);
  const dot1Color = useTransform(scrollYProgress, [0.33, 0.36, 0.67], ["rgba(100,80,60,0.3)", "rgba(179,150,120,0.9)", "rgba(100,80,60,0.3)"]);
  const dot2Color = useTransform(scrollYProgress, [0.64, 0.67, 1], ["rgba(100,80,60,0.3)", "rgba(179,150,120,0.9)", "rgba(179,150,120,0.9)"]);

  const PHONE_W = 260,PHONE_H = 540;
  const accent = "#B39678";

  return (
    <div
      ref={containerRef}
      style={{ position: "relative", height: "300vh" }}>
      {/* Sticky viewport */}
      <div style={{ position: "sticky", top: 0, height: "100vh", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: "100%", maxWidth: 1200, margin: "0 auto", padding: "0 48px", display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 60 }}>

          {/* Left text — fase 0 e 1 */}
          <div style={{ position: "relative", minHeight: 220 }}>
            {/* Fase 0: accumulo punti */}
            <motion.div style={{ opacity: phase0Opacity, position: "absolute", top: 0, left: 0, width: "100%" }}>
              <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#B39678", marginBottom: 12 }}>
                La tua Card Vantaggi
              </p>
              <h2 style={{ fontSize: "clamp(2rem, 3vw, 3rem)", fontWeight: 800, lineHeight: 1.05, letterSpacing: "-0.02em", color: "#fff", margin: "0 0 20px" }}>
                Ogni acquisto<br />diventa un punto
              </h2>
              <p style={{ fontSize: 17, color: "rgba(255,255,255,0.65)", lineHeight: 1.65, margin: 0, maxWidth: 380 }}>
                Accumula punti ad ogni visita in negozio.<br />
                Il tuo saldo cresce insieme al tuo arredamento.
              </p>
              <div style={{ marginTop: 32, display: "flex", gap: 12 }}>
                <InfoBadge
                  opacity={phase0Opacity}
                  icon={<Star size={16} color={accent} />}
                  title="Punti accumulati"
                  sub="Da ogni acquisto"
                  style={{ position: "relative", inset: "auto" }} />
                
              </div>
            </motion.div>

            {/* Fase 1: QR */}
            <motion.div style={{ opacity: phase1Opacity, position: "absolute", top: 0, left: 0, width: "100%" }}>
              <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#B39678", marginBottom: 12 }}>
                Il tuo QR Code
              </p>
              <h2 style={{ fontSize: "clamp(2rem, 3vw, 3rem)", fontWeight: 800, lineHeight: 1.05, letterSpacing: "-0.02em", color: "#fff", margin: "0 0 20px" }}>
                La tua identità<br />in un gesto
              </h2>
              <p style={{ fontSize: 17, color: "rgba(255,255,255,0.65)", lineHeight: 1.65, margin: 0, maxWidth: 380 }}>
                Mostra il QR code al personale di negozio<br />
                per registrare i tuoi acquisti istantaneamente.
              </p>
              <div style={{ marginTop: 32, display: "flex", gap: 12 }}>
                <InfoBadge
                  opacity={phase1Opacity}
                  icon={<QrCode size={16} color={accent} />}
                  title="Scansione rapida"
                  sub="Sempre con te"
                  style={{ position: "relative", inset: "auto" }} />
                
              </div>
            </motion.div>
          </div>

          {/* Phone */}
          <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", width: PHONE_W + 80, height: PHONE_H + 80 }}>
            {/* Glow */}
            <motion.div
              style={{
                position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)",
                width: 200, height: 50, borderRadius: "50%",
                background: "rgba(179,150,120,0.35)", filter: "blur(24px)",
                opacity: useTransform(rewardsOpacity, [0, 1], [0.4, 0.8])
              }} />
            <PhoneFrame
              screenFront={phoneScreen}
              screenBack={phoneScreen}
              screenRewards={phoneScreen}
              cardFlipProgress={cardFlipProgress}
              rewardsOpacity={rewardsOpacity}
              width={PHONE_W}
              height={PHONE_H} />
            
          </div>

          {/* Right text — fase 2 */}
          <div style={{ position: "relative", minHeight: 220 }}>
            <motion.div style={{ opacity: phase2Opacity, position: "absolute", top: 0, left: 0, width: "100%" }}>
              <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#86694B", marginBottom: 12 }}>
                Le tue Ricompense
              </p>
              <h2 style={{ fontSize: "clamp(2rem, 3vw, 3rem)", fontWeight: 800, lineHeight: 1.05, letterSpacing: "-0.02em", color: "#2d1a0a", margin: "0 0 20px" }}>
                Trasforma i punti<br />in vantaggi reali
              </h2>
              <p style={{ fontSize: 17, color: "#6A6661", lineHeight: 1.65, margin: 0, maxWidth: 380 }}>
                Voucher sconto, prodotti esclusivi e molto altro.<br />
                I tuoi punti hanno un valore concreto.
              </p>
              <div style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 10 }}>
                <InfoBadge
                  opacity={b1}
                  icon={<Percent size={15} color={accent} />}
                  title="Voucher sconto"
                  sub="Usa in negozio"
                  style={{ position: "relative", inset: "auto", background: "rgba(255,255,255,0.92)", border: "1px solid rgba(179,150,120,0.3)" }} />
                
                <InfoBadge
                  opacity={b2}
                  icon={<Gift size={15} color={accent} />}
                  title="Your Selection"
                  sub="Prodotti esclusivi"
                  style={{ position: "relative", inset: "auto", background: "rgba(255,255,255,0.92)", border: "1px solid rgba(179,150,120,0.3)" }} />
                
                <InfoBadge
                  opacity={b3}
                  icon={<Tag size={15} color={accent} />}
                  title="Offerte dedicate"
                  sub="Solo per te"
                  style={{ position: "relative", inset: "auto", background: "rgba(255,255,255,0.92)", border: "1px solid rgba(179,150,120,0.3)" }} />
                
              </div>
            </motion.div>
          </div>
        </div>

        {/* Progress dots */}
        <div style={{ position: "absolute", bottom: 36, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 8, alignItems: "center" }}>
          {([dot0, dot1, dot2] as MotionValue<number>[]).map((w, i) =>
          <motion.div key={i} style={{ width: w, height: 6, borderRadius: 99, background: [dot0Color, dot1Color, dot2Color][i] }} />
          )}
        </div>
      </div>
    </div>);

}

/* ── Main ── */
export default function DesktopLanding() {
  return (
    <div
      role="main"
      className="relative"
      style={{ fontFamily: "'Koblenz-Medium Regular', sans-serif" }}>

      {/* ── SEZIONE 1: Hero ── */}
      <div
        className="relative flex flex-col"
        style={{
          backgroundImage: `url(${saloneBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "100vh",
        }}>

        {/* Overlay — si estende fino al footer */}
        <div
          className="absolute inset-0 pointer-events-none z-0"
          style={{ background: "linear-gradient(180deg, rgba(5,3,1,0.62) 0%, rgba(10,6,2,0.52) 55%, rgba(5,3,1,0.82) 100%)" }} />

        {/* Navbar */}
        <div className="relative z-10 flex items-center px-12 h-20 shrink-0">
          <motion.img src={logoOrizzontale} alt="Centrolegno Arreda" className="h-28 w-auto" fetchPriority="high" {...fadeIn(0.1)} />
        </div>

        {/* Hero — flex-1 per occupare tutto lo spazio disponibile */}
        <section className="relative z-10 flex items-center flex-1">
          <div className="w-full max-w-7xl mx-auto px-6 md:px-8 lg:px-10 xl:px-12 grid grid-cols-2 items-center gap-8 lg:gap-12 xl:gap-16 2xl:gap-24">

            <div className="flex flex-col gap-6 lg:gap-8 xl:gap-10">
              <motion.div {...fadeUp(0.25)} className="flex flex-col gap-0">
                <h1 style={{ margin: 0, fontSize: "clamp(1.6rem, 3vw, 4rem)", lineHeight: 1, letterSpacing: "-0.03em", fontWeight: 800, color: "#fff", whiteSpace: "nowrap" }}>
                  Arreda la tua casa
                </h1>
                <p style={{ margin: 0, marginBottom: "10px", fontSize: "clamp(1.1rem, 1.8vw, 2rem)", letterSpacing: "0.01em", fontWeight: 600 }} className="text-primary-soft">
                  con chi la progetta davvero !
                </p>
                <div style={{ width: 60, height: 1.5, background: "#B39678", opacity: 0.8 }} />
              </motion.div>

              <motion.p {...fadeUp(0.35)} className="text-base lg:text-lg leading-relaxed max-w-md text-primary-soft">
                Sfoglia il catalogo, accumula punti fedeltà e scopri le promozioni
                esclusive direttamente dal tuo smartphone.
              </motion.p>

              <motion.div {...fadeUp(0.45)} className="flex flex-wrap gap-3">
                <StoreButton variant="white" icon={<AppleIcon />} store="App Store" sub="Scarica su" />
                <StoreButton variant="glass" icon={<PlayIcon />} store="Google Play" sub="Disponibile su" />
              </motion.div>

              <motion.div {...fadeUp(0.55)} className="flex items-center gap-4 pt-2">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => <Star key={i} size={13} fill="#B39678" stroke="none" />)}
                </div>
                <p className="text-sm" style={{ color: "rgba(255,255,255,0.45)" }}>
                  Il tuo arredamento, a portata di mano!
                </p>
              </motion.div>
            </div>

            <motion.div {...fadeIn(0.5)} className="flex justify-center items-center min-w-0">
              <Phone3DInteractive />
            </motion.div>
          </div>
        </section>

        {/* ── MINI FOOTER — sfumatura liquida senza bordo ── */}
        {/* Pseudo-strato sfumato che "emerge" dallo sfondo verso il footer */}
        <div style={{
          position: "relative",
          zIndex: 10,
          height: 40,
          marginBottom: -1,
          background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.28) 100%)",
          pointerEvents: "none",
        }} />
        <motion.footer
          {...fadeIn(0.8)}
          className="relative z-10 shrink-0"
          style={{
            background: "rgba(0,0,0,0.28)",
            backdropFilter: "blur(40px) saturate(1.8) brightness(1.05)",
            WebkitBackdropFilter: "blur(40px) saturate(1.8) brightness(1.05)",
          }}>
          <div className="w-full max-w-7xl mx-auto px-6 md:px-10 xl:px-12 py-4 flex flex-col md:flex-row items-center justify-between gap-3">

            {/* Ragione sociale + dati legali */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
              <span style={{ color: "rgba(255,255,255,0.88)", fontSize: 12, fontWeight: 700, letterSpacing: "0.04em" }}>
                Centrolegno Arreda Srl
              </span>
              <span style={{ color: "rgba(255,255,255,0.22)", fontSize: 11 }}>|</span>
              <span style={{ color: "rgba(255,255,255,0.52)", fontSize: 11 }}>
                P.IVA &amp; C.F. 02078210859
              </span>
              <span style={{ color: "rgba(255,255,255,0.22)", fontSize: 11 }}>|</span>
              <span style={{ color: "rgba(255,255,255,0.52)", fontSize: 11 }}>
                REA CL-118276
              </span>
              <span style={{ color: "rgba(255,255,255,0.22)", fontSize: 11 }}>|</span>
              <span style={{ color: "rgba(255,255,255,0.52)", fontSize: 11 }}>
                Viale Luigi Monaco, 35 — Caltanissetta (CL)
              </span>
            </div>

            {/* Email assistenza */}
            <a
              href="mailto:assistenza-app@centrolegnoarreda.it"
              style={{
                color: "rgba(255,255,255,0.92)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.03em",
                textDecoration: "none",
                whiteSpace: "nowrap",
                transition: "opacity 0.2s",
              }}
              onMouseEnter={e => (e.currentTarget.style.opacity = "0.65")}
              onMouseLeave={e => (e.currentTarget.style.opacity = "1")}>
              assistenza-app@centrolegnoarreda.it
            </a>
          </div>
        </motion.footer>

      </div>

    </div>);

}